import { useEffect, useState } from 'react';
import { IconCheck, IconCircleAlert, IconCloudOff, IconWifiOff } from '../icons';
import { tn } from '../i18n';
import { useTranslate } from '../i18n/react';
import { outbox } from '../offline/store';
import { sync, type SyncOutcome } from '../offline/sync';

/**
 * Whether anything is still on the device.
 *
 * <p>The one thing an auditor needs to know and currently cannot: a visit filled in with no
 * signal looks exactly like a visit that has been sent. Four states, and they are not the
 * same thing — no network, a network but things still queued, something the server refused,
 * and nothing owed.</p>
 *
 * <p>`navigator.onLine` is only half the answer. It says the device has a network, not that
 * our server can be reached through it, which is exactly the difference on hotel wifi that
 * answers DHCP and nothing else. So the queue depth is the real signal and the flag is only
 * the hint.</p>
 */
export function SyncChip({ outcome }: { outcome?: SyncOutcome | null }) {
  const t = useTranslate();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);

    window.addEventListener('online', up);
    window.addEventListener('offline', down);

    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  useEffect(() => {
    let stopped = false;

    const count = () =>
      void outbox.all().then((rows) => {
        if (!stopped) setPending(rows.length);
      });

    count();
    // Cheap, and the alternative is threading the queue depth through every screen that
    // writes to it.
    const timer = window.setInterval(count, 4000);

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [outcome]);

  if (!online) {
    return (
      <span className="nv-sync nv-sync--offline" role="status">
        <IconWifiOff size={14} />
        {t('sync.offline')}
      </span>
    );
  }

  if (outcome && outcome.rejected.length > 0) {
    return (
      <span className="nv-sync nv-sync--error" role="status">
        <IconCircleAlert size={14} />
        {t('sync.failed')}
        <button type="button" className="nv-sync__retry" onClick={() => void sync()}>
          {t('common.retry')}
        </button>
      </span>
    );
  }

  if (pending > 0) {
    return (
      <span className="nv-sync nv-sync--pending" role="status">
        <IconCloudOff size={14} />
        {tn('sync.pending', pending)}
      </span>
    );
  }

  return (
    <span className="nv-sync" role="status">
      <IconCheck size={14} />
      {t('sync.synced')}
    </span>
  );
}
